
import { useEffect, useRef }  from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';
import { useToast } from '@/components/ui/use-toast';
import { useMountSuffix } from './useMountSuffix';

export const useRealtimeSubscriptions = () => {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const channelsRef = useRef<any[]>([]);
  const suffix = useMountSuffix();

  useEffect(() => {
    let isMounted = true;
    channelsRef.current = [];
    console.log('[useRealtimeSubscriptions] setup start — suffix:', suffix);

    const cleanup = () => {
      isMounted = false;
      console.log('[useRealtimeSubscriptions] cleanup start — channels:', channelsRef.current.map((ch: any) => ch?.topic || ch?.name || '?'));
      channelsRef.current.forEach((ch) => {
        try {
          console.log('[useRealtimeSubscriptions] cleanup — removing channel:', ch?.topic || ch?.name || '?');
          // Explicitly remove all postgres_changes listeners before unsubscribing
          try {
            if (typeof ch.off === 'function') {
              ch.off('postgres_changes');
            }
          } catch (e) {
            console.warn("Error removing channel listeners:", e);
          }
          // Clear any remaining bindings to prevent lingering callbacks
          try {
            if (ch.bindings) {
              ch.bindings = {};
            }
          } catch (e) {
            // noop
          }
          ch.unsubscribe();
          supabase.removeChannel(ch);
        } catch (err) {
          console.warn("Error cleaning up channel:", err);
        }
      });
      channelsRef.current = [];
      console.log('[useRealtimeSubscriptions] cleanup complete');
    };

    try {
      let warnedOffline = false;
      const notifyOffline = () => {
        if (warnedOffline) return;
        warnedOffline = true;
        try {
          toast({
            title: "Mises à jour en temps réel indisponibles",
            description: "L'application continue de fonctionner normalement.",
          });
        } catch (e) {
          console.warn("Toast failed:", e);
        }
      };

      const reportsChannelName = `reports-${suffix}`;
      console.log('[useRealtimeSubscriptions] creating channel:', reportsChannelName);
      const channel = supabase
        .channel(reportsChannelName)
        .on(
          'postgres_changes',
          {
            event: '*',
            schema: 'public',
            table: 'reports'
          },
          (payload) => {
            if (!isMounted) return;
            try {
              console.log(`[${reportsChannelName}] received payload:`, payload);
              queryClient.invalidateQueries({ queryKey: ['reports'] });

              const messages = {
                INSERT: "Un nouveau signalement a été ajouté",
                UPDATE: "Un signalement a été mis à jour",
                DELETE: "Un signalement a été supprimé"
              };

              toast({
                title: "Mise à jour en temps réel",
                description: messages[payload.eventType as keyof typeof messages],
              });
            } catch (err) {
              console.error(`[${reportsChannelName}] error handling payload:`, err);
            }
          }
        )
        .subscribe((status) => {
          console.log(`[${reportsChannelName}] subscription status:`, status);
          if (status === 'CHANNEL_ERROR' || status === 'TIMED_OUT') {
            console.warn(`[${reportsChannelName}] realtime unavailable`);
            notifyOffline();
          }
        });

      const supportsChannelName = `supports-${suffix}`;
      console.log('[useRealtimeSubscriptions] creating channel:', supportsChannelName);
      const supportsChannel = supabase
        .channel(supportsChannelName)
        .on(
          'postgres_changes',
          {
            event: '*',
            schema: 'public',
            table: 'supports'
          },
          (payload) => {
            if (!isMounted) return;
            try {
              console.log(`[${supportsChannelName}] received payload:`, payload);
              queryClient.invalidateQueries({ queryKey: ['userSupports'] });
              queryClient.invalidateQueries({ queryKey: ['reports'] });
              queryClient.invalidateQueries({ queryKey: ['support'] });
            } catch (err) {
              console.error(`[${supportsChannelName}] error handling payload:`, err);
            }
          }
        )
        .subscribe((status) => {
          console.log(`[${supportsChannelName}] subscription status:`, status);
          if (status === 'CHANNEL_ERROR' || status === 'TIMED_OUT') {
            console.warn(`[${supportsChannelName}] realtime unavailable`);
            notifyOffline();
          }
        });

      channelsRef.current = [channel, supportsChannel];

      return cleanup;
    } catch (error) {
      console.error("Error setting up realtime subscriptions:", error);
      try {
        toast({
          title: "Mises à jour en temps réel indisponibles",
          description: "L'application continue de fonctionner normalement.",
        });
      } catch (e) {
        console.warn("Toast failed:", e);
      }
      return cleanup;
    }
  }, [queryClient, toast]);
};
